// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import { ICertificationWebpagePersonalDataApiResponse } from 'interfaces/webpage/certification/registration/personal-data/personal-data-api.interface'
import {
  ICertificationWebpagePersonalDataGetDetailAttrs,
  ICertificationWebpagePersonalDataUpdateAttrs,
} from 'interfaces/webpage/certification/registration/personal-data/personal-data-view.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_companyDataProps, withHelper_profileDataProps } from 'models/extensions/with-helper'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { model, optional } = types

// Loading
const { BASE, ACTION } = LOADING

// Profile Data Model
const ProfileData = optional(model('ProfileData').props(withHelper_profileDataProps), {})

// Company Data Model
const CompanyData = optional(model('CompanyData').props(withHelper_companyDataProps), {})

// Personal Data Model
const PersonalDataDetail = optional(
  model('PersonalData').props({
    profileData: ProfileData,
    companyData: CompanyData,
  }),
  {},
)

const CertificationWebpagePersonalDataModel = types
  .model('CertificationWebpagePersonalData')
  .props({
    certificationWebpagePersonalData_loading: withHelper_commonLoadingModel,
    certificationWebpagePersonalData_detail: PersonalDataDetail,
  })
  .actions(self => ({
    certificationWebpagePersonalData_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpagePersonalData_loading, payload)

      detach(self.certificationWebpagePersonalData_loading)
      self.certificationWebpagePersonalData_loading = cast(map)
    },
    certificationWebpagePersonalData_SET_DETAIL: (payload: ICertificationWebpagePersonalDataApiResponse['res']['data']): void => {
      self.certificationWebpagePersonalData_detail = cast(payload)
    },
    certificationWebpagePersonalData_CLEAR_DETAIL: (): void => {
      self.certificationWebpagePersonalData_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch process date detail
     *
     * @param {ICertificationWebpagePersonalDataGetDetailAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePersonalData_fetchDetail: flow(function* (payload: ICertificationWebpagePersonalDataGetDetailAttrs) {
      self.certificationWebpagePersonalData_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpagePersonalDataApiResponse =
        yield self.environment.certificationWebpagePersonalDataApi.certificationWebpagePersonalData_getDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpagePersonalData_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePersonalData_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Update personal data
     *
     * @param {ICertificationWebpagePersonalDataUpdateAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePersonalData_update: flow(function* (payload: ICertificationWebpagePersonalDataUpdateAttrs) {
      self.certificationWebpagePersonalData_SET_LOADING({ type: ACTION, value: true })

      const response: ICertificationWebpagePersonalDataApiResponse =
        yield self.environment.certificationWebpagePersonalDataApi.certificationWebpagePersonalData_update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpagePersonalData_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePersonalData_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createCertificationWebpagePersonalDataDefaultModel = () => optional(CertificationWebpagePersonalDataModel, {})
